// editor/hooks/slide-arrival.ts
//
// CONTENT ARRIVING ON A SLIDE, as an animation.
//
// Written for "Add with AI", where one slide fills in over a couple of
// seconds, and now shared with the funnel's deck build, where a whole
// document is written slide by slide (see how-to-start/deck-build-runner).
// Both want the same thing -- elements resolving into place rather than
// appearing pasted -- and one copy of it means the two cannot drift.
//
// It is DOM only. The class is applied imperatively to element wrappers,
// never written into an entity's settings, so it cannot be saved into the
// deck, exported, or seen by a reader.
//
// The order of operations matters and is easy to get wrong:
//
//   1. holdInvisible(ids)  -- before the write, so the finished content never
//                             paints for a frame or two before being animated
//   2. dispatch the change
//   3. two frames later:  reveal(ids) and releaseHold() TOGETHER, so the
//                         elements are never briefly unstyled
//
// Two frames, not one: React has to commit before the node exists to animate,
// and on a replace the old node is gone while the new one is not mounted yet.

import styles from "./progressive-slide.module.scss";

/** How long one element's arrival animation runs; matches the stylesheet. */
export const ARRIVAL_DURATION_MS = 520;

/**
 * The wait before the first element moves.
 *
 * The animation's `both` fill means adding the class HIDES the element until
 * its delay elapses -- which is the whole trick: the content is swapped while
 * it is invisible, so the reader never sees the change happen, only the
 * result arriving. That requires the store dispatch and React's re-render to
 * land inside this window. A frame's worth of entity edits measured around
 * 60ms, so this is roughly double.
 */
export const ARRIVAL_LEAD_MS = 120;

/**
 * One thing to bring in.
 *
 * A plain id gets the full treatment -- rise, blur, fade. `soft` gets opacity
 * only, for a container whose children are animating inside it; see the
 * stylesheet for why those must not compound.
 *
 * A unit can name a NODE instead of an id because only element entities carry
 * their id into the DOM. The card a set draws has no id of its own, so the
 * caller finds the node and passes it directly.
 */
export type RevealUnit =
  | string
  | { id: string; soft?: boolean }
  | { node: HTMLElement; soft?: boolean };

const nodeOf = (unit: RevealUnit): HTMLElement | null => {
  if (typeof unit === "string") return document.getElementById(unit);
  if ("node" in unit) return unit.node;
  return document.getElementById(unit.id);
};

export type CascadeOptions = {
  /** Gap between one element starting and the next. */
  stepMs?: number;
  /** Elements after this many share the last slot, so a dense slide stays brisk. */
  maxSteps?: number;
  /** Wait before the first element moves. */
  leadMs?: number;
};

const HIDE_STYLE_ID = "sd-ai-arrival-hold";

/**
 * Hide these entities until the reveal starts.
 *
 * `visibility` rather than `display`, so the slide keeps its layout and
 * nothing reflows when the content becomes visible again.
 */
export const holdInvisible = (ids: string[]): void => {
  releaseHold();
  if (ids.length === 0) return;
  const style = document.createElement("style");
  style.id = HIDE_STYLE_ID;
  style.textContent = `${ids
    .map((id) => `#${CSS.escape(id)}`)
    .join(",")}{visibility:hidden!important}`;
  document.head.appendChild(style);
};

/** Hide a whole slide's contents by its root id, for a slide being inserted. */
export const holdSubtreeInvisible = (rootId: string): void => {
  releaseHold();
  if (!rootId) return;
  const style = document.createElement("style");
  style.id = HIDE_STYLE_ID;
  const root = `#${CSS.escape(rootId)}`;
  style.textContent = `${root},${root} *{visibility:hidden!important}`;
  document.head.appendChild(style);
};

export const releaseHold = (): void => {
  document.getElementById(HIDE_STYLE_ID)?.remove();
};

/**
 * Play the arrival animation on these entities, one after another.
 *
 * Returns how long the whole cascade will take, so a caller that has to wait
 * for it -- the deck build, which will not start the next slide until this
 * one has finished -- does not have to work the timing out again.
 */
export const revealArriving = (
  units: RevealUnit[],
  { stepMs = 70, maxSteps = 5, leadMs = ARRIVAL_LEAD_MS }: CascadeOptions = {},
): number => {
  let step = 0;
  let last = leadMs;
  for (const unit of units) {
    const soft = typeof unit === "string" ? false : Boolean(unit.soft);
    const arrival = soft ? styles.appearingSoft : styles.appearing;
    const el = nodeOf(unit);
    if (!el) continue;
    // Only elements that actually exist get a slot in the cascade, so a
    // missing node does not leave a gap in the timing.
    const delay = leadMs + Math.min(step, maxSteps) * stepMs;
    last = delay;
    el.style.setProperty("--sd-ai-delay", `${delay}ms`);
    step++;
    // Restart cleanly when a slot changes twice in one build: removing the
    // class alone does not rewind a running animation, and the reflow read
    // between remove and add is what forces it.
    el.classList.remove(arrival);
    void el.offsetWidth;
    el.classList.add(arrival);
    el.addEventListener(
      "animationend",
      () => {
        el.classList.remove(arrival);
        // The delay is only meaningful for the run that set it; leaving it
        // behind would hold the element invisible on the next one.
        el.style.removeProperty("--sd-ai-delay");
      },
      { once: true },
    );
  }
  return step === 0 ? 0 : last + ARRIVAL_DURATION_MS;
};

/**
 * Take the arrival animation back off these entities.
 *
 * `animationend` already does this, and cannot be relied on alone: the editor
 * virtualises slides, so a slide scrolled out of view is unmounted and the
 * event for anything still animating in it never arrives. The class then
 * survives on the entity, and when that slide is scrolled back INTO view the
 * animation plays again -- text blurring and shifting for no reason, which
 * reads as the document warping while you move around it.
 *
 * So the caller clears the slide it just built, rather than trusting each
 * element to clear itself.
 */
export const clearArriving = (units: RevealUnit[]): void => {
  for (const unit of units) {
    const el = nodeOf(unit);
    if (!el) continue;
    el.classList.remove(styles.appearing, styles.appearingSoft);
    el.style.removeProperty("--sd-ai-delay");
  }
};

/**
 * Take the arrival animation off EVERYTHING, wherever it got to.
 *
 * Clearing slide by slide is not enough on its own: the editor virtualises
 * slides, so by the time a slide has finished building the user may already
 * have scrolled past it, and there is no node left to clear. The class then
 * comes back with the node -- and a re-mounted element with `.appearing`
 * still on it replays the whole blur-and-shift on scroll.
 *
 * Called once when a build ends, so nothing is left animating behind it.
 */
export const clearAllArriving = (): void => {
  document
    .querySelectorAll<HTMLElement>(
      `.${styles.appearing},.${styles.appearingSoft}`,
    )
    .forEach((el) => {
      el.classList.remove(styles.appearing, styles.appearingSoft);
      el.style.removeProperty("--sd-ai-delay");
    });
  document
    .querySelectorAll<HTMLElement>('[style*="--sd-ai-delay"]')
    .forEach((el) => el.style.removeProperty("--sd-ai-delay"));
};

/** How long a cascade over `count` elements will take, before running it. */
export const cascadeDuration = (
  count: number,
  { stepMs = 70, maxSteps = 5, leadMs = ARRIVAL_LEAD_MS }: CascadeOptions = {},
): number =>
  count === 0
    ? 0
    : leadMs + Math.min(count - 1, maxSteps) * stepMs + ARRIVAL_DURATION_MS;

// ─── a whole slide, assembling ─────────────────────────────────────────
//
// Moved here from how-to-start/deck-build-runner so the agent's deck build
// (agent/handlers/build-deck) assembles its slides exactly the same way.

/** Gap between one piece of a slide starting to appear and the next. */
export const SLIDE_BUILD_STEP_MS = 95;

/**
 * Pieces past this many share the last slot in the cascade.
 *
 * A dense slide would otherwise take most of a minute on its own, and by then
 * the movement has stopped reading as construction. High enough that a normal
 * slide -- a title and three or four cards -- cascades all the way through.
 */
export const SLIDE_BUILD_MAX_STEPS = 16;

/** Pause after a slide has finished building, before the next one starts. */
export const SLIDE_SETTLE_MS = 280;

export const prefersReducedMotion = (): boolean =>
  typeof window !== "undefined" &&
  !!window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches;

/**
 * Two frames, so React has committed and the new nodes exist to animate —
 * or FRAME_FALLBACK_MS, whichever comes first. A tab in the background gets
 * no frames at all, and a deck build waiting on them stopped dead until the
 * user came back to it.
 */
const FRAME_FALLBACK_MS = 150;
export const afterTwoFrames = (): Promise<void> =>
  new Promise<void>((resolve) => {
    if (typeof requestAnimationFrame !== "function") return resolve();
    const timer = setTimeout(resolve, FRAME_FALLBACK_MS);
    requestAnimationFrame(() =>
      requestAnimationFrame(() => {
        clearTimeout(timer);
        resolve();
      }),
    );
  });

/**
 * WHAT A SLIDE IS BUILT OUT OF, in the order it should assemble.
 *
 * Animating only the elements was not enough: the CARDS they sit in are
 * containers, so the whole layout appeared complete the instant the slide was
 * revealed and only the words faded in afterwards. What the reader sees first
 * is the structure, so the structure is most of the effect -- the slide read
 * as arriving whole.
 *
 * So containers come in too, each just before its own contents: card, then
 * its icon, then its heading, then its text; then the next card. A container
 * is marked `soft`, meaning it only fades -- it is animating while its
 * children animate inside it, and two nested rises and blurs compound into
 * the slide appearing to warp.
 *
 * Groups are skipped. They are layout wrappers with nothing of their own to
 * show, and giving them a slot only slows the build down.
 */
export const buildUnitsOf = (
  rootId: string,
  layers: Record<string, { type?: string; childIds?: string[] }> | undefined,
): RevealUnit[] => {
  if (!layers) return [];

  const elementsUnder = (id: string, out: string[] = []): string[] => {
    const node = layers[id];
    if (!node) return out;
    if (node.type === "element") out.push(id);
    for (const child of node.childIds ?? []) elementsUnder(child, out);
    return out;
  };

  const all = elementsUnder(rootId);
  const units: RevealUnit[] = [];
  const walk = (id: string) => {
    const node = layers[id];
    if (!node) return;
    if (node.type === "set") {
      const inside = elementsUnder(id);
      // A set holding one thing draws no card worth showing separately; the
      // element's own arrival is the whole of it.
      if (inside.length > 1) {
        const card = commonAncestorOf(
          inside,
          all.filter((other) => !inside.includes(other)),
        );
        if (card) units.push({ node: card, soft: true });
      }
    }
    if (node.type === "element") units.push(id);
    for (const child of node.childIds ?? []) walk(child);
  };
  walk(rootId);
  return units;
};

/**
 * The node that draws a card, found from the elements inside it.
 *
 * Only element entities carry their id into the DOM -- a set has no node of
 * its own to look up -- so the card is identified as the nearest thing that
 * contains all of the set's elements and nothing else's.
 */
const commonAncestorOf = (
  ids: string[],
  outsiders: string[],
): HTMLElement | null => {
  const nodes = ids
    .map((id) => document.getElementById(id))
    .filter((n): n is HTMLElement => n !== null);
  if (nodes.length < 2) return null;
  let ancestor: HTMLElement | null = nodes[0].parentElement;
  while (ancestor && !nodes.every((n) => ancestor?.contains(n))) {
    ancestor = ancestor.parentElement;
  }
  if (!ancestor) return null;
  // REFUSE AN ANCESTOR THAT IS BIGGER THAN THE CARD. A set whose elements
  // share no tighter wrapper walks up to the slide itself, and fading THAT is
  // the whole slide arriving at once -- the thing this is meant to replace.
  const swallowsOthers = outsiders.some((id) => {
    const other = document.getElementById(id);
    return other ? ancestor?.contains(other) : false;
  });
  return swallowsOthers ? null : ancestor;
};
